import * as React from 'react';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  makeStyles
} from '@material-ui/core';
import {
  HeaderCell,
  SearchBar,
  SecondaryAlert,
  SecondaryButton,
  Table,
  TableBody,
  TableHead,
  TertiaryButton
} from 'kagr-ui';
import { useParams, useLocation } from 'react-router-dom';
import { TableSortState } from '../../../models/tableSortState';
import { DateTimeFormatter } from '../../../shared/date-format';
import Pagination from '../../pagination/pagination';
import * as styles from '../segments.module.scss';
import { useGetCampaignTerm, useGetPushRuns, useStopAutomaticPush } from '../../../hooks/useSegmentPush';
import StopIcon from '@material-ui/icons/Stop';
import PushSegmentBreadcrumbs from '../breadcrumbs/push-segment-breadcrumbs';
import useTimedAlerts from '../../../hooks/useTimedAlerts';
import RunStatus from '../../../models/enums/runStatus';
import PushStatus from '../../../models/enums/pushStatus';
import clsx from 'clsx';

enum PushDetailsSortFields {
  DateCreated = 'dateCreated',
  TotalCustomers = 'totalCustomers',
  CustomersPushed = 'customersPushed',
  Status = 'status'
}

const pushDetailsHeaders = [
  {
    title: 'Date/Time',
    orderField: PushDetailsSortFields.DateCreated
  },
  {
    title: '# Customers in Segment',
    orderField: PushDetailsSortFields.TotalCustomers
  },
  {
    title: '# Customers Added to',
    orderField: PushDetailsSortFields.CustomersPushed
  },
  {
    title: 'Status',
    orderField: PushDetailsSortFields.Status
  }
];

const useStyles = makeStyles(() => ({
  tableHeader: {
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '30px 30px 0 0',
    display: 'flex'
  },
  pushInfo: {
    display: 'flex',
    padding: '10px',
    backgroundColor: '#F9F9F9',
    alignItems: 'center'
  },
  infoContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '5px 40px 5px 10px'
  },
  infoTitle: {
    color: '#4F4F4F',
    fontSize: '12px',
    marginBottom: '10px',
    textTransform: 'uppercase'
  },
  infoValue: {
    color: '#4F4F4F',
    fontSize: '16px',
    fontWeight: 600,
    minHeight: '20px'
  },
  stop: {
    color: '#C92100',
    border: '3px solid',
    borderRadius: '20px',
    cursor: 'pointer'
  },
  stopDisabled: {
    color: 'gray',
    cursor: 'not-allowed'
  },
  pushMessage: {
    position: 'absolute',
    top: '20px',
    left: 0,
    right: 0,
    marginLeft: 'auto',
    marginRight: 'auto',
    width: 'fit-content'
  },
  dialogActions: {
    justifyContent: 'space-between',
    height: 57,
    padding: '0 24px'
  }
}));

const ViewPushDetails = () => {
  const classes = useStyles();
  const { segmentId, pushId } = useParams<{ segmentId: string; pushId: string }>();
  const { state } = useLocation<{ clientIdForSegment: number }>();
  const { successMessage, setSuccessMessage, errorMessage, setErrorMessage } = useTimedAlerts();
  const [tableSort, setTableSort] = React.useState<TableSortState>({
    page: 1,
    pageSize: 15,
    orderColumn: 'dateCreated',
    orderDirection: 'desc'
  });
  const [searchValue, setSearchValue] = React.useState<string>('');
  const [activeSearchTerm, setActiveSearchTerm] = React.useState<string>('');
  const [showStopDialog, setShowStopDialog] = React.useState<boolean>(false);

  const pushQuery = useGetPushRuns(segmentId, pushId, tableSort, activeSearchTerm);
  const stopAutomaticPush = useStopAutomaticPush();
  const campaignTermQuery = useGetCampaignTerm(
    pushQuery?.data?.pushDestinationId.toString(),
    state.clientIdForSegment?.toString()
  );
  const [campaignTerm, setCampaignTerm] = React.useState<string>('Campaign');

  function reorderHandler(column: string, direction: 'asc' | 'desc') {
    setTableSort((state) => ({ ...state, orderColumn: column, orderDirection: direction }));
  }

  React.useEffect(() => {
    if (campaignTermQuery.data) {
      setCampaignTerm(campaignTermQuery.data);
    }
  }, [campaignTermQuery.data]);

  const stopAutomaticCampaign = () => {
    setShowStopDialog(false);
    stopAutomaticPush.mutate(
      {
        segmentId: segmentId,
        pushId: pushId,
        clientId: state.clientIdForSegment?.toString()
      },
      {
        onSuccess(results) {
          if (results.success) {
            setSuccessMessage('Push successfully stopped');
          } else {
            setErrorMessage('Failed to stop push');
          }
        },
        onError() {
          setErrorMessage('There was an error stopping the push');
        }
      }
    );
  };

  const setHeaderTitle = (title: string): string => {
    if (title === '# Customers Added to') {
      return `${title} ${campaignTerm}`;
    }
    return title;
  };

  return (
    <React.Fragment>
      {errorMessage && (
        <div className={classes.pushMessage}>
          <SecondaryAlert type="error" message={errorMessage} />
        </div>
      )}
      {successMessage && (
        <div className={classes.pushMessage}>
          <SecondaryAlert type="success" message={successMessage} />
        </div>
      )}
      <div className={styles.root}>
        <PushSegmentBreadcrumbs currentPage={'View Push'} />
        <Paper elevation={1} className={styles.paper}>
          <div className={styles.content}>
            <div className={classes.tableHeader}>
              <div className={classes.pushInfo}>
                <div className={classes.infoContainer}>
                  <span className={classes.infoTitle}>Push Id</span>
                  <span className={classes.infoValue}>{pushId}</span>
                </div>
                <div className={classes.infoContainer}>
                  <span className={classes.infoTitle}>Destination</span>
                  <span className={classes.infoValue}>{pushQuery.data?.pushDestinationName}</span>
                </div>
                <div className={classes.infoContainer}>
                  <span className={classes.infoTitle}>{campaignTerm} Id</span>
                  <span className={classes.infoValue}>{pushQuery.data?.campaignId}</span>
                </div>
                {pushQuery.data && pushQuery.data.status !== PushStatus['N/A'] && (
                  <>
                    {pushQuery.data.status === PushStatus.Active && (
                      <StopIcon className={classes.stop} onClick={() => setShowStopDialog(true)} />
                    )}
                    {pushQuery.data.status === PushStatus.Stopped && (
                      <StopIcon className={clsx(classes.stop, classes.stopDisabled)} />
                    )}
                  </>
                )}
              </div>
              <SearchBar
                value={searchValue}
                updateValue={setSearchValue}
                placeholder="Search by Name"
                submitHandler={() => setActiveSearchTerm(searchValue)}
              />
            </div>
            {pushQuery.isLoading && !pushQuery.data && (
              <div className={styles.loading}>
                <CircularProgress />
              </div>
            )}
            {pushQuery.isError && (
              <div className={styles.error}>
                <SecondaryAlert type="error" message={`Failed to fetch results: ${pushQuery.error.message}`} />
              </div>
            )}
            {pushQuery.data && (
              <div className={styles.tableWrapper}>
                <Table
                  className={styles.table}
                  loading={pushQuery.isPreviousData && pushQuery.isFetching}
                  results={pushQuery.data.runs.results}
                >
                  <TableHead>
                    {pushDetailsHeaders.map((header) => (
                      <HeaderCell
                        key={header.title}
                        title={setHeaderTitle(header.title)}
                        orderField={header.orderField ? header.orderField : null}
                        orderColumn={header.orderField ? tableSort.orderColumn : null}
                        orderDirection={header.orderField ? tableSort.orderDirection : null}
                        reorderHandler={header.orderField ? reorderHandler : null}
                      />
                    ))}
                  </TableHead>
                  <TableBody>
                    {pushQuery.data.runs.totalItems === 0 && (
                      <tr>
                        <td colSpan={pushQuery.data.runs.results.length}>No results found</td>
                      </tr>
                    )}
                    {pushQuery.data.runs.results.map((run: any) => (
                      <tr key={`${run.pushId}`}>
                        <td className={styles.lastModifiedColumn}>
                          {DateTimeFormatter.format(new Date(run.dateCreated))}
                        </td>
                        <td>{run.totalCustomers}</td>
                        <td>{run.customersPushed}</td>
                        <td>{RunStatus[run.status]}</td>
                      </tr>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {pushQuery.data && (
              <div className={styles.paginationFooter}>
                <Pagination
                  totalPages={Math.ceil(pushQuery.data.runs.totalItems / tableSort.pageSize)}
                  pageSize={tableSort.pageSize}
                  currentPage={tableSort.page}
                  disabled={pushQuery.isPreviousData && pushQuery.isFetching}
                  handlePageChange={(page) => setTableSort((state) => ({ ...state, page }))}
                  handlePageSizeChange={(pageSize) => setTableSort((state) => ({ ...state, pageSize }))}
                />
              </div>
            )}
          </div>
        </Paper>
      </div>
      <Dialog open={showStopDialog}>
        <DialogTitle disableTypography>Stop Automatic Run?</DialogTitle>
        <DialogContent>Are you sure you want to stop this automatic run?</DialogContent>
        <DialogActions className={classes.dialogActions}>
          <TertiaryButton
            text="Cancel"
            title="Cancel"
            component="button"
            handleClick={() => setShowStopDialog(false)}
          />
          <SecondaryButton text="Stop" title="Stop" handleClick={stopAutomaticCampaign} />
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default ViewPushDetails;
